import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnInit,
  AfterContentChecked,
  Output,
  ChangeDetectorRef,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  ObjectType,
  TranslationModule,
  CrmObjectService,
  SystemFieldIds,
  StandardIds,
  StandardExternalIds,
  CrmFieldService,
  TranslateForCrmObjectService,
} from '@galaxy/crm/static';
import { MatButtonModule } from '@angular/material/button';
import { BulkImportStepperService, CsvRow, PermissionType } from '../bulk-import-stepper.service';
import { combineLatest, forkJoin, Observable, of, ReplaySubject, shareReplay } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { MatTableModule } from '@angular/material/table';
import { CrmFieldMappingRowComponent } from './field-mapping-row.component';
import { FieldMappingService, FieldSchemaGroup } from './field-mapping.service';
import {
  CSVHeaderMappingInterface,
  FieldSchemaInterface,
  FieldType,
  ValidateCrmObjectResponseInterface,
  ValidateCrmObjectResponseValidationErrorSeverity as Severity,
  CustomObjectTypeInterface,
  CrmObjectInterface,
} from '@vendasta/crm';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CrmDependencies, CrmInjectionToken } from '@galaxy/crm/static';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { FieldValueInterface } from '@vendasta/crm/lib/_internal/interfaces/crm-objects.interface';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';

const CUSTOM_OBJECT_PREFIX = 'CustomObject::';
const CUSTOM_OBJECT_DELIMITER = '::';

@Component({
  selector: 'crm-field-mapping-step',
  templateUrl: 'field-mapping-step.component.html',
  providers: [],
  styleUrls: ['field-mapping-step.component.scss'],
  imports: [
    CommonModule,
    TranslationModule,
    MatButtonModule,
    MatTableModule,
    MatFormFieldModule,
    CrmFieldMappingRowComponent,
    GalaxyFormFieldModule,
    MatSelectModule,
    MatOption,
    MatIcon,
    GalaxyTooltipModule,
  ],
})
export class CrmFieldMappingStepComponent implements OnInit, AfterContentChecked {
  private readonly config: CrmDependencies = inject(CrmInjectionToken);
  private readonly stepperService = inject(BulkImportStepperService);
  private readonly fieldMapping = inject(FieldMappingService);
  private readonly objectService = inject(CrmObjectService);
  private readonly translate = inject(TranslateService);
  private readonly translateCrmObject = inject(TranslateForCrmObjectService);
  private readonly cdref = inject(ChangeDetectorRef);
  protected readonly crmFieldService = inject(CrmFieldService);

  protected readonly displayedColumns = ['header', 'firstRow', 'importAs', 'mapTo'];
  protected importTypeValues: { [key: number]: ObjectType } = {};
  private importTypeValues$$!: ReplaySubject<{ [key: number]: ObjectType }>;

  dataSource$: Observable<MappingTableData[]> = this.stepperService.csvFirstRow$.pipe(
    map((firstRow) =>
      Object.entries(firstRow).map(([key, value]) => ({
        header: key,
        firstRow: value.value,
        isDuplicateHeaderName: value.isDuplicateHeaderName,
      })),
    ),
  );
  isPartnerCenter = this.config.appID === 'partner-center-client';
  instructionsText$ = combineLatest([
    this.translate.stream('BULK_IMPORT.MAP_FIELDS_STEP.INSTRUCTIONS'),
    this.translate.stream('BULK_IMPORT.MAP_FIELDS_STEP.PARTNER_INSTRUCTIONS'),
  ]).pipe(
    map(([instructions, partnerInstructions]) => {
      if (this.isPartnerCenter) {
        return `${instructions} ${partnerInstructions}`;
      }
      return instructions;
    }),
  );
  protected companyFieldMappingGroups$!: Observable<FieldSchemaGroup[]>;
  protected contactFieldMappingGroups$!: Observable<FieldSchemaGroup[]>;
  protected customObjectFieldMappingGroups$!: Observable<FieldSchemaGroup[]>;
  protected opportunitiesFieldMappingGroups$!: Observable<FieldSchemaGroup[]>;
  protected fieldMappingGroupsLoading$!: Observable<boolean>;
  private readonly fieldMappings$: Observable<CSVHeaderMappingInterface[]> = this.fieldMapping.fieldMappings$;
  protected mappingsErrors$!: Observable<string[]>;
  protected opportunityWarning$!: Observable<string>;
  @Output() fieldMappingsErrors = new EventEmitter<string[]>();

  private readonly destroyRef = inject(DestroyRef);

  completeStep(): void {
    this.stepperService.completeStep('fieldMappingCompleted', true);
  }

  blockStep(): void {
    this.stepperService.completeStep('fieldMappingCompleted', false);
  }

  addValidationValue(
    mapping: CSVHeaderMappingInterface,
    firstRow: CsvRow,
    validationRow: FieldValueInterface[],
    fieldSchemaMap: Map<string, FieldSchemaInterface>,
  ): void {
    if (!mapping.fieldId || !mapping.headerName || !firstRow[mapping.headerName]) {
      return;
    }

    const fieldType = fieldSchemaMap.get(mapping.fieldId)?.fieldType;
    if (!fieldType) {
      return;
    }

    const foundTagIndex = validationRow.findIndex((vRow) => vRow.fieldId === mapping.fieldId);
    const validationValue = this.getValidationValue(mapping, fieldType, firstRow[mapping.headerName].value);

    if (!validationValue) {
      if (fieldType === FieldType.FIELD_TYPE_TAG && foundTagIndex !== -1) {
        const existingTag = validationRow[foundTagIndex];
        if (existingTag.stringValues?.values) {
          existingTag.stringValues.values.push(firstRow[mapping.headerName].value);
        }
      }
      return;
    }

    validationRow.push(validationValue);
  }

  getValidationValue(
    mapping: CSVHeaderMappingInterface,
    fieldType: FieldType,
    firstRowValue: string,
  ): FieldValueInterface | null {
    switch (fieldType) {
      case FieldType.FIELD_TYPE_EMAIL:
      case FieldType.FIELD_TYPE_PHONE:
      case FieldType.FIELD_TYPE_STRING:
        return {
          fieldId: mapping.fieldId,
          stringValue: firstRowValue,
        };
      case FieldType.FIELD_TYPE_INTEGER:
        return {
          fieldId: mapping.fieldId,
          integerValue: parseInt(firstRowValue, 10),
        };
      case FieldType.FIELD_TYPE_DATE:
        return {
          fieldId: mapping.fieldId,
          dateValue: new Date(firstRowValue),
        };
      case FieldType.FIELD_TYPE_BOOLEAN:
        return {
          fieldId: mapping.fieldId,
          booleanValue: !!firstRowValue,
        };
      case FieldType.FIELD_TYPE_TAG:
        return {
          fieldId: mapping.fieldId,
          stringValues: {
            values: [firstRowValue],
          },
        };
      default:
        break;
    }
    return null;
  }

  getErrorMessages(
    validation: ValidateCrmObjectResponseInterface,
    objectType?: ObjectType,
    fieldSchemaMap?: Map<string, FieldSchemaInterface>,
    subtypeName?: string,
  ): Observable<string[]> {
    const severeErrors = (validation.errors ?? []).filter(
      (e) => e.severity === Severity.VALIDATION_ERROR_SEVERITY_INVALID && !e.details?.uniquenessCheck,
    );

    if (severeErrors.length === 0) {
      return of([]);
    }

    const errorObservables = severeErrors.map((error) => {
      const fieldId = error.details?.fieldIds && error.details.fieldIds.length > 0 ? error.details.fieldIds[0] : '';

      let fieldName = '';
      if (fieldSchemaMap && fieldId) {
        const fieldSchema = fieldSchemaMap.get(fieldId);
        fieldName = fieldSchema?.fieldName || fieldId;
      }

      if (subtypeName) {
        if (objectType && error.message) {
          return this.translateCrmObject.getTranslationForCrmObject(objectType, error.message, {
            label: fieldName || fieldId,
            customObjectName: subtypeName,
          });
        }
        return of(
          this.translate.instant(error.message || '', {
            customObjectName: subtypeName,
            label: fieldName || fieldId,
          }),
        );
      }

      if (objectType && error.message) {
        return this.translateCrmObject.getTranslationForCrmObject(objectType, error.message, {
          label: fieldName || fieldId,
        });
      }
      return of(this.translate.instant(error.message || ''));
    });

    return forkJoin(errorObservables);
  }

  ngOnInit(): void {
    // done so that importTypeValues is reset when parsing a new file
    this.importTypeValues$$ = this.fieldMapping.getImportTypeValues();
    this.importTypeValues$$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
      this.importTypeValues = values;
    });

    this.contactFieldMappingGroups$ = this.stepperService.contactPermissions$.pipe(
      switchMap((canAccess) => {
        if (canAccess) {
          return this.fieldMapping.listFieldGroups$('Contact');
        } else {
          return of([]);
        }
      }),
      shareReplay(1),
    );
    this.companyFieldMappingGroups$ = this.stepperService.companyPermissions$.pipe(
      switchMap((canAccess) => {
        if (canAccess) {
          return this.fieldMapping.listFieldGroups$('Company');
        } else {
          return of([]);
        }
      }),
      shareReplay(1),
    );
    this.opportunitiesFieldMappingGroups$ = this.stepperService.opportunitiesPermissions$.pipe(
      switchMap((canAccess) => {
        if (canAccess && !this.isPartnerCenter) {
          return this.fieldMapping.listFieldGroups$('Opportunity');
        } else {
          return of([]);
        }
      }),
      shareReplay(1),
    );
    this.customObjectFieldMappingGroups$ = this.stepperService.customObjectPermissions$.pipe(
      map(() => []), //Custom object field groups are handled by subtype in the row component
      shareReplay(1),
    );
    this.fieldMappingGroupsLoading$ = combineLatest([
      this.companyFieldMappingGroups$,
      this.contactFieldMappingGroups$,
      this.customObjectFieldMappingGroups$,
      this.opportunitiesFieldMappingGroups$,
    ]).pipe(
      map(
        ([
          companyFieldMappingGroups,
          contactFieldMappingGroups,
          customObjectFieldMappingGroups,
          opportunityFieldMappingGroups,
        ]) => {
          return (
            !companyFieldMappingGroups ||
            !contactFieldMappingGroups ||
            !customObjectFieldMappingGroups ||
            !opportunityFieldMappingGroups
          );
        },
      ),
    );

    this.mappingsErrors$ = combineLatest([
      this.stepperService.csvFirstRow$,
      this.stepperService.contactPermissions$,
      this.stepperService.companyPermissions$,
      this.stepperService.customObjectPermissions$,
      this.stepperService.opportunitiesPermissions$,
      this.fieldMappings$,
      this.importTypeValues$$,
    ]).pipe(
      switchMap(
        ([
          firstRow,
          contactPermissions,
          companyPermissions,
          customObjectPermissions,
          opportunityPermissions,
          mappings,
          _,
        ]) => {
          const contactFieldSchemaMap$ = contactPermissions
            ? this.fieldMapping.getFieldSchemaMap$('Contact')
            : of(new Map<string, FieldSchemaInterface>());
          const companyFieldSchemaMap$ = companyPermissions
            ? this.fieldMapping.getFieldSchemaMap$('Company')
            : of(new Map<string, FieldSchemaInterface>());

          const opportunityFieldSchemaMap$ =
            opportunityPermissions && !this.isPartnerCenter
              ? this.fieldMapping.getFieldSchemaMap$('Opportunity')
              : of(new Map<string, FieldSchemaInterface>());

          const customObjectSubtypes: string[] = [];
          for (const mapping of mappings) {
            if (mapping.crmObjectType === 'CustomObject' && mapping.crmObjectSubtype) {
              if (!customObjectSubtypes.includes(mapping.crmObjectSubtype)) {
                customObjectSubtypes.push(mapping.crmObjectSubtype);
              }
            }
          }

          const customObjectFieldSchemaMaps$ =
            !customObjectPermissions || customObjectSubtypes.length === 0
              ? of(new Map<string, Map<string, FieldSchemaInterface>>())
              : forkJoin(
                  customObjectSubtypes.map((subtype) =>
                    this.fieldMapping
                      .getFieldSchemaMap$(`${CUSTOM_OBJECT_PREFIX}${subtype}` as ObjectType)
                      .pipe(map((fieldSchemaMap) => ({ subtype, fieldSchemaMap }))),
                  ),
                ).pipe(
                  map(
                    (subtypeMaps) =>
                      new Map(subtypeMaps.map(({ subtype, fieldSchemaMap }) => [subtype, fieldSchemaMap])),
                  ),
                );

          return forkJoin([
            of(firstRow),
            contactFieldSchemaMap$,
            companyFieldSchemaMap$,
            customObjectFieldSchemaMaps$,
            opportunityFieldSchemaMap$,
            of(mappings),
          ]);
        },
      ),
      map(
        ([
          firstRow,
          contactFieldSchemaMap,
          companyFieldSchemaMap,
          customObjectFieldSchemaMaps,
          opportunityFieldSchemaMap,
          mappings,
        ]) => {
          const contactValidationRow: FieldValueInterface[] = [];
          const companyValidationRow: FieldValueInterface[] = [];
          const opportunityValidationRow: FieldValueInterface[] = [];
          const customObjectValidationRows = new Map<string, FieldValueInterface[]>();
          const selectedCustomObjectSubtypes = Object.values(this.importTypeValues)
            .filter((value): value is ObjectType => typeof value === 'string' && value.startsWith(CUSTOM_OBJECT_PREFIX))
            .map((value) => value.split(CUSTOM_OBJECT_DELIMITER)[1]);

          selectedCustomObjectSubtypes.forEach((subtype) => {
            customObjectValidationRows.set(subtype, []);
          });

          for (const mapping of mappings) {
            switch (mapping.crmObjectType) {
              case 'Contact':
                this.addValidationValue(mapping, firstRow, contactValidationRow, contactFieldSchemaMap);
                break;
              case 'Company':
                this.addValidationValue(mapping, firstRow, companyValidationRow, companyFieldSchemaMap);
                break;
              case 'CustomObject':
                if (mapping.crmObjectSubtype) {
                  const subtypeValidationRow = customObjectValidationRows.get(mapping.crmObjectSubtype);
                  const fieldSchemaMap = customObjectFieldSchemaMaps.get(mapping.crmObjectSubtype);
                  if (subtypeValidationRow && fieldSchemaMap) {
                    this.addValidationValue(mapping, firstRow, subtypeValidationRow, fieldSchemaMap);
                  }
                }
                break;
              case 'Opportunity':
                this.addValidationValue(mapping, firstRow, opportunityValidationRow, opportunityFieldSchemaMap);
                break;
              default:
                break;
            }
          }

          customObjectValidationRows.forEach((subtypeValidationRow, subtype) => {
            const validatedSubtypeRow = this.checkForCustomObjectTypeIDs(subtypeValidationRow);
            customObjectValidationRows.set(subtype, validatedSubtypeRow);
          });

          return {
            contactValidationRow,
            companyValidationRow,
            customObjectValidationRows,
            opportunityValidationRow,
            firstRow,
            customObjectFieldSchemaMaps,
            contactFieldSchemaMap,
            companyFieldSchemaMap,
            opportunityFieldSchemaMap,
          };
        },
      ),
      switchMap(
        ({
          contactValidationRow,
          companyValidationRow,
          customObjectValidationRows,
          opportunityValidationRow,
          customObjectFieldSchemaMaps,
          contactFieldSchemaMap,
          companyFieldSchemaMap,
          opportunityFieldSchemaMap,
        }) => {
          const hasContactSelected = Object.values(this.importTypeValues).includes('Contact');
          const hasCompanySelected = Object.values(this.importTypeValues).includes('Company');
          const hasOpportunitySelected = Object.values(this.importTypeValues).includes('Opportunity');
          const hasCustomObjectSelected = Object.values(this.importTypeValues).some(
            (value) => typeof value === 'string' && value.startsWith(CUSTOM_OBJECT_PREFIX),
          );

          // only get validation errors if there are fields to validate
          const contactValidation$ =
            hasContactSelected && contactValidationRow.length > 0
              ? this.objectService
                  .validateCrmObject('Contact', {
                    fields: this.checkForContactIDs(contactValidationRow),
                  })
                  .pipe(map((validation) => ({ validation, fieldSchemaMap: contactFieldSchemaMap })))
              : of({ validation: { errors: [] }, fieldSchemaMap: contactFieldSchemaMap });
          const companyValidation$ =
            hasCompanySelected && companyValidationRow.length > 0
              ? this.objectService
                  .validateCrmObject('Company', {
                    fields: this.checkForCompanyIDs(companyValidationRow),
                  })
                  .pipe(map((validation) => ({ validation, fieldSchemaMap: companyFieldSchemaMap })))
              : of({ validation: { errors: [] }, fieldSchemaMap: companyFieldSchemaMap });

          const opportunityValidation$ =
            hasOpportunitySelected && opportunityValidationRow.length > 0
              ? this.objectService
                  .validateCrmObject('Opportunity', {
                    fields: this.checkForOpportunityIDs(opportunityValidationRow),
                  })
                  .pipe(map((validation) => ({ validation, fieldSchemaMap: opportunityFieldSchemaMap })))
              : of({ validation: { errors: [] }, fieldSchemaMap: opportunityFieldSchemaMap });

          const customObjectValidation$ = !hasCustomObjectSelected
            ? of({ validation: { responses: [], subtypes: [] }, fieldSchemaMap: customObjectFieldSchemaMaps })
            : (() => {
                const validationPromises: Observable<ValidateCrmObjectResponseInterface>[] = [];
                const subtypes: string[] = [];
                customObjectValidationRows.forEach((subtypeValidationRow, subtype) => {
                  if (subtypeValidationRow.length > 0) {
                    validationPromises.push(
                      this.objectService.validateCrmObject('CustomObject', {
                        fields: subtypeValidationRow,
                        crmObjectSubtype: subtype,
                      } as CrmObjectInterface),
                    );
                    subtypes.push(subtype);
                  }
                });

                return validationPromises.length > 0
                  ? forkJoin(validationPromises).pipe(
                      map((responses) => {
                        return { validation: { responses, subtypes }, fieldSchemaMap: customObjectFieldSchemaMaps };
                      }),
                    )
                  : of({ validation: { responses: [], subtypes: [] }, fieldSchemaMap: customObjectFieldSchemaMaps });
              })();

          const NoImportTypesValidation$ =
            !hasContactSelected && !hasCompanySelected && !hasCustomObjectSelected && !hasOpportunitySelected
              ? of('BULK_IMPORT.MAP_FIELDS_STEP.NO_IMPORT_TYPES_SELECTED')
              : of('');

          return combineLatest([
            contactValidation$,
            companyValidation$,
            customObjectValidation$,
            opportunityValidation$,
            NoImportTypesValidation$,
          ]);
        },
      ),
      switchMap(
        ([
          contactValidation,
          companyValidation,
          customObjectValidation,
          opportunityValidation,
          NoImportTypesValidation,
        ]) => {
          // special case when no import types have been selected
          if (NoImportTypesValidation) {
            return of([this.translate.instant(NoImportTypesValidation)]);
          }

          return combineLatest([
            this.getErrorMessages(contactValidation.validation, 'Contact', contactValidation.fieldSchemaMap),
            this.getErrorMessages(companyValidation.validation, 'Company', companyValidation.fieldSchemaMap),
            this.getErrorMessages(
              opportunityValidation.validation,
              'Opportunity',
              opportunityValidation.fieldSchemaMap,
            ),
          ]).pipe(
            switchMap(
              ([
                severeContactFieldMappingsErrors,
                severeCompanyFieldMappingsErrors,
                severeOpportunityFieldMappingsErrors,
              ]) => {
                const severeCustomObjectFieldMappingsErrors: string[] = [];

                if (customObjectValidation.validation.responses.length > 0) {
                  return this.customObjectTypes().pipe(
                    switchMap((customObjectTypes) => {
                      const customObjectErrorObservables: Observable<string[]>[] = [];

                      customObjectValidation.validation.responses.forEach((response, index) => {
                        const subtype = customObjectValidation.validation.subtypes[index];
                        const customObjectType = customObjectTypes.find((type) => type.customObjectTypeId === subtype);
                        const subtypeName = customObjectType?.singularObjectName || '';
                        const subtypeFieldSchemaMap = customObjectValidation.fieldSchemaMap.get(subtype);

                        customObjectErrorObservables.push(
                          this.getErrorMessages(response, 'CustomObject', subtypeFieldSchemaMap, subtypeName),
                        );
                      });

                      return forkJoin(customObjectErrorObservables).pipe(
                        map((customObjectErrors) => {
                          const allCustomObjectErrors = customObjectErrors.flat();
                          return [
                            ...severeContactFieldMappingsErrors,
                            ...severeCompanyFieldMappingsErrors,
                            ...allCustomObjectErrors,
                            ...severeOpportunityFieldMappingsErrors,
                          ];
                        }),
                      );
                    }),
                  );
                }

                return of([
                  ...severeContactFieldMappingsErrors,
                  ...severeCompanyFieldMappingsErrors,
                  ...severeCustomObjectFieldMappingsErrors,
                  ...severeOpportunityFieldMappingsErrors,
                ]);
              },
            ),
          );
        },
      ),
      tap((errorMessages) => {
        // emit errors for tooltip in bulk-import-stepper
        this.fieldMappingsErrors.emit(errorMessages);
      }),
      shareReplay(1),
    );

    this.opportunityWarning$ = this.importTypeValues$$.pipe(
      map((importTypeValues) => {
        const hasOpportunitySelected = Object.values(importTypeValues).includes('Opportunity');
        const hasContactSelected = Object.values(importTypeValues).includes('Contact');
        const hasCompanySelected = Object.values(importTypeValues).includes('Company');
        const hasCustomObjectSelected = Object.values(importTypeValues).some(
          (value) => typeof value === 'string' && value.startsWith(CUSTOM_OBJECT_PREFIX),
        );

        if (hasOpportunitySelected && !hasContactSelected && !hasCompanySelected && !hasCustomObjectSelected) {
          return this.translate.instant('BULK_IMPORT.MAP_FIELDS_STEP.OPPORTUNITY_NO_ASSOCIATION');
        }
        return '';
      }),
      shareReplay(1),
    );

    this.mappingsErrors$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((errorMessages) => {
          if (errorMessages.length) {
            this.blockStep();
          } else {
            this.completeStep();
          }
        }),
      )
      .subscribe();
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }

  hasPermission$(permissionType: PermissionType) {
    return this.stepperService.hasPermission$(permissionType);
  }

  updateImportTypeValues(key: number, value: ObjectType) {
    this.importTypeValues[key] = value;
    this.importTypeValues$$.next(this.importTypeValues);
  }

  // Will check if ID is present, if it is and the name is not, set a non-empty value to skip the requirement validation
  checkForContactIDs(contactValidationRow: FieldValueInterface[]): FieldValueInterface[] {
    if (
      contactValidationRow.some((contactField) => contactField?.fieldId === SystemFieldIds.ContactID) &&
      !contactValidationRow.some(
        (contactField) => contactField?.fieldId === this.crmFieldService.getFieldId(StandardExternalIds.Email),
      )
    ) {
      return [
        ...contactValidationRow,
        {
          fieldId: this.crmFieldService.getFieldId(StandardExternalIds.Email),
          stringValue: 'NON_EMPTY_VALUE',
        },
      ];
    }
    return contactValidationRow;
  }

  // Will check if ID is present, if it is and the name is not, set a non-empty value to skip the requirement validation
  checkForCompanyIDs(companyValidationRow: FieldValueInterface[]): FieldValueInterface[] {
    if (
      companyValidationRow.some((contactField) => contactField?.fieldId === SystemFieldIds.CompanyID) &&
      !companyValidationRow.some((contactField) => contactField?.fieldId === StandardIds.CompanyName)
    ) {
      return [
        ...companyValidationRow,
        {
          fieldId: StandardIds.CompanyName,
          stringValue: 'NON_EMPTY_VALUE',
        },
      ];
    }
    return companyValidationRow;
  }

  // Will check if ID is present, if it is and the name is not, set a non-empty value to skip the requirement validation
  checkForCustomObjectTypeIDs(customObjectValidationRow: FieldValueInterface[]): FieldValueInterface[] {
    if (
      customObjectValidationRow.some((field) => field?.fieldId === SystemFieldIds.CustomObjectID) &&
      !customObjectValidationRow.some((field) => field?.fieldId === StandardIds.CustomObjectName)
    ) {
      return [
        ...customObjectValidationRow,
        {
          fieldId: StandardIds.CustomObjectName,
          stringValue: 'NON_EMPTY_VALUE',
        },
      ];
    }
    return customObjectValidationRow;
  }

  // Will check if ID is present, if it is and the name is not, set a non-empty value to skip the requirement validation
  checkForOpportunityIDs(opportunityValidationRow: FieldValueInterface[]): FieldValueInterface[] {
    if (
      opportunityValidationRow.some((opportunityField) => opportunityField?.fieldId === SystemFieldIds.OpportunityID) &&
      !opportunityValidationRow.some((opportunityField) => opportunityField?.fieldId === StandardIds.OpportunityName)
    ) {
      return [
        ...opportunityValidationRow,
        {
          fieldId: StandardIds.OpportunityName,
          stringValue: 'NON_EMPTY_VALUE',
        },
      ];
    }

    return opportunityValidationRow;
  }

  customObjectTypes(): Observable<CustomObjectTypeInterface[]> {
    return this.stepperService.customObjectTypes$;
  }
}

interface MappingTableData {
  header: string;
  firstRow?: string;
}
